import { isProdLike } from "../../shared/constants";
import { ensureError } from "../../shared/utils/ensureError";

import { BridgeError, type Capabilities, type ImportResult, type LensStudioBridge, type UploadResult } from "./types";

interface QWebChannelSignal {
    connect(handler: (json: string) => void): void;
    disconnect(handler: (json: string) => void): void;
}

/** Shape of the bridge object exposed by C++ via QWebChannel. */
interface QWebChannelBridgeObject {
    importCompleted: QWebChannelSignal;
    uploadCompleted: QWebChannelSignal;
    getCapabilities(callback: (result: string) => void): void;
    importAssets(json: string): void;
    uploadAsset(json: string): void;
    openExternalUrl(url: string): void;
}

interface QWebChannelResult {
    objects: { bridge?: QWebChannelBridgeObject };
}

declare global {
    interface Window {
        qt?: { webChannelTransport: unknown };
        QWebChannel?: QWebChannelFactory;
    }
}

type QWebChannelFactory = new (transport: unknown, callback: (channel: QWebChannelResult) => void) => void;

const ensureQWebChannel = async (): Promise<QWebChannelFactory | null> => {
    // Qt injects qt.webChannelTransport when running inside QWebEngineView.
    if (!window.qt?.webChannelTransport) return null;

    // QWebChannel class is injected by C++ via QWebEngineScript at DocumentCreation time.
    // Do NOT try to load qrc:///qtwebchannel/qwebchannel.js via <script> tag — qrc: URLs
    // are not accessible from HTTPS-served pages. The C++ side handles injection.
    // See RemoteAssetsWebPage.cpp for the QWebEngineScript setup.
    if (window.QWebChannel) return window.QWebChannel;

    // QWebChannel may not be on window yet if DocumentCreation script hasn't run.
    // Wait briefly — it should be available before DOMContentLoaded.
    // Returns the factory if it appears during the poll, or null on timeout.
    return new Promise<QWebChannelFactory | null>((resolve) => {
        if (window.QWebChannel) {
            resolve(window.QWebChannel);
            return;
        }
        const check = setInterval(() => {
            if (window.QWebChannel) {
                clearInterval(check);
                resolve(window.QWebChannel);
            }
        }, 10);
        setTimeout(() => {
            clearInterval(check);
            resolve(null);
        }, 1000);
    });
};

/**
 * Creates a queued method that serializes calls through a QWebChannel signal.
 * Each call waits for the previous one to complete before invoking, preventing
 * concurrent signal handlers from receiving each other's responses.
 */
const createQueuedSignalMethod = <T>(
    signal: QWebChannelSignal,
    invoke: (payload: string) => void,
): ((payload: string) => Promise<T>) => {
    let queue: Promise<unknown> = Promise.resolve();
    return (payload: string) => {
        const task = () =>
            new Promise<T>((res, rej) => {
                const handler = (resultJson: string) => {
                    signal.disconnect(handler);
                    try {
                        res(JSON.parse(resultJson) as T);
                    } catch (error) {
                        rej(ensureError(error));
                    }
                };
                signal.connect(handler);
                invoke(payload);
            });
        queue = queue.then(task, task);
        return queue as Promise<T>;
    };
};

const createQWebChannelBridge = async (): Promise<LensStudioBridge | null> => {
    const QWebChannel = await ensureQWebChannel();
    if (!QWebChannel) {
        // If Qt transport is present, QWebChannel should have been injected by C++.
        // Its absence means the bridge script failed to load — surface this as an error
        // instead of silently falling back to SSO (which can't work in the embed).
        if (window.qt?.webChannelTransport) {
            throw new BridgeError(
                "BRIDGE_NOT_FOUND",
                "Qt transport detected but QWebChannel was not injected. Check C++ QWebEngineScript setup.",
            );
        }
        return null;
    }

    // ensureQWebChannel() only returns non-null when window.qt.webChannelTransport exists.
    // Narrow the type via an explicit check so TypeScript and eslint can both verify safety.
    const { webChannelTransport: transport } = window.qt ?? {};
    if (!transport) return null;

    return new Promise<LensStudioBridge>((resolve, reject) => {
        const timeout = setTimeout(() => {
            reject(new BridgeError("BRIDGE_TIMEOUT", "Bridge initialization timed out"));
        }, 5000);

        new QWebChannel(transport, (channel) => {
            clearTimeout(timeout);
            const bridgeObj = channel.objects.bridge;
            if (!bridgeObj) {
                reject(new BridgeError("BRIDGE_NOT_FOUND", "Bridge object not registered"));
                return;
            }

            // Queues ensure signal-based operations run sequentially.
            // QWebChannel signals broadcast to all connected handlers, so
            // concurrent calls would cause all pending promises to resolve
            // with the first signal's payload.
            const queuedImport = createQueuedSignalMethod<ImportResult>(bridgeObj.importCompleted, (p) => {
                bridgeObj.importAssets(p);
            });
            const queuedUpload = createQueuedSignalMethod<UploadResult>(bridgeObj.uploadCompleted, (p) => {
                bridgeObj.uploadAsset(p);
            });

            resolve({
                getCapabilities: () =>
                    new Promise((res, rej) => {
                        bridgeObj.getCapabilities((result: string) => {
                            try {
                                res(JSON.parse(result) as Capabilities);
                            } catch (error) {
                                rej(error instanceof Error ? error : new Error(String(error)));
                            }
                        });
                    }),
                importAssets: (assetIds) => queuedImport(JSON.stringify(assetIds)),
                uploadAsset: (options) => queuedUpload(JSON.stringify(options)),
                openExternalUrl: (url: string) => {
                    bridgeObj.openExternalUrl(url);
                },
            });
        });
    });
};

/**
 * Attempts to initialize the bridge to the embedding host.
 *
 * Production: Uses QWebChannel to communicate with the C++ bridge.
 * Dev/testing: Mock bridge via ?mock-bridge=true URL param.
 *
 * Returns null when no bridge is available.
 */
export const initBridge = async (): Promise<LensStudioBridge | null> => {
    // Mock bridge for dev/testing (non-production only)
    if (!isProdLike()) {
        const params = new URLSearchParams(window.location.search);
        if (params.get("mock-bridge") === "true") {
            const { createMockBridge } = await import("./mockBridge");
            return createMockBridge();
        }
    }

    // Try QWebChannel (Lens Studio embed)
    try {
        return await createQWebChannelBridge();
    } catch (error) {
        if (error instanceof BridgeError) throw error;
        // QWebChannel not available — no bridge
        return null;
    }
};
